<script lang="ts">
	import { mergeAttributes } from '@vpap_svelte/internal';
	import { ChartContext } from './ChartDimensions.svelte.js';
	import { ActiveLayer, SvgManager } from './Managers.svelte.js';

	import type { SVGElementProps } from '@vpap_svelte/internal';
	import type { Snippet } from 'svelte';

	interface SVGProps extends Omit<SVGElementProps<SVGSVGElement>, 'children'> {
		/**
		 * The `title` element or a string title.
		 *
		 * (This is named `titleEl` instead of
		 * title because there is technically an SVG `title` attribute. That said,
		 * [you probably shouldn't use the title attribute](https://www.a11yproject.com/posts/title-attributes/).)
		 */
		titleEl?: Snippet<[SvgManager]> | string;
		/**
		 * The `defs` element for the SVG. You can also access this element through the SVG's context.
		 * (You would then append stuff to the `defs` element using a portal
		 */
		defs?: Snippet<[SvgManager]>;
		/**
		 * The child snippet, with context
		 */
		children?: Snippet<[SvgManager]>;
		/**
		 * Whether the element is designed to be used in `ssr` contexts
		 * @default false
		 */
		ssr?: boolean;
	}

	let {
		children,
		titleEl,
		defs,
		ssr = false,
		viewBox,
		preserveAspectRatio,
		...attrs
	}: SVGProps = $props();
	let chartCtx = ChartContext.get();

	let element: SVGSVGElement | undefined = $state(undefined);
	let defsEl: SVGDefsElement | undefined = $state(undefined);
	let componentProps = $derived.by(() => {
		if (ssr === true) {
			return {
				viewBox: '0 0 100 100',
				preserveAspectRatio: 'none',
				class: 'non-scaling-stroke',
				height: '100%',
				width: '100%',
			} satisfies SVGElementProps<SVGSVGElement>;
		} else {
			const chart = chartCtx.chart;
			const w = chart.isInitial ? '100%' : `${chart.width}px`;
			const h = chart.isInitial ? '100%' : `${chart.height}px`;
			if (
				viewBox === undefined &&
				preserveAspectRatio === undefined &&
				w !== '100%' &&
				h !== '100%'
			) {
				return {
					width: w,
					height: h,
					viewBox: `0 0 ${w} ${h}`,
					preserveAspectRatio: 'xMidYMid meet',
				};
			}
			return {
				width: w,
				height: h,
				viewBox,
				preserveAspectRatio,
			} satisfies SVGElementProps<SVGSVGElement>;
		}
	});
	// NOTE: these are spread so that any changes to them trigger a re-calculation
	let svgAttrs = $derived(mergeAttributes({ ...attrs }, { ...componentProps }));

	let manager = new SvgManager({ element: () => element, defs: () => defsEl, ssr: () => ssr });

	ActiveLayer.set(manager);
</script>

<svg bind:this={element} {...svgAttrs}>
	{#if titleEl}
		{#if typeof titleEl === 'string'}
			<title>{titleEl}</title>
		{:else}
			{@render titleEl(manager)}
		{/if}
	{/if}
	{#if defs !== undefined || manager.patterns.size > 0}
		<defs bind:this={defsEl}>
			{#if defs}{@render defs(manager)}{/if}
			{#each manager.patterns.entries() as [id, pattern] (id)}
				{@render pattern(manager)}
			{/each}
		</defs>
	{/if}
	{#if children}
		{@render children(manager)}
	{/if}
</svg>

<style>
	svg {
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		overflow: visible;
	}

	svg.non-scaling-stroke :global(*) {
		vector-effect: non-scaling-stroke;
	}
</style>
